<script lang="ts" setup>
import DefaultReviewerSelectorData from '@/components/common/invoices/default-reviewers-selector/data/DefaultReviewerSelectorData.vue';
import DebitNormsData from '@/components/common/projects/debit-norms/data/DebitNormsData.vue';
import { PhBuildings, PhInfo, PhStairs, PhTable } from '@phosphor-icons/vue';
import { hasRole } from 'cesg-shared';
import { UserRole } from 'cesg-shared/dist/models';
import { toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverviewLogic } from './logic/useOverviewLogic.ts';
import AfterMarketTableData from './sub-components/after-market-table/data/AfterMarketTableData.vue';
import LiftPlanUEListData from './sub-components/lift-plan-ue-list/data/LiftPlanUEListData.vue';
import LiftPlanData from './sub-components/lift-plan/data/LiftPlanData.vue';
import { OverviewAtaTables } from './sub-components/overview-ata-tables';
import OverviewSection from '@/components/common/OverviewSection.vue';
import ProjectMetaDataData from './sub-components/project-meta-data/data/ProjectMetaDataData.vue';
import ProjectNotesData from './sub-components/project-notes/data/ProjectNotesData.vue';
import ProjectOrganisationsData from './sub-components/project-organisations/data/ProjectOrganisationsData.vue';
import SensitiveValuesSwitchData from './sub-components/sensitive-values-switch/data/SensitiveValuesSwitchData.vue';
import type { OverviewViewProps } from './types.ts';

const props = defineProps<OverviewViewProps>();
const { state, action, mainRef } = useOverviewLogic(props);
const { t } = useI18n();
</script>

<template>
  <div
    class="overview-side-header"
    v-if="state.user && hasRole(UserRole.CESG, toValue(state.user))"
  >
    <SensitiveValuesSwitchData
      :project-id="state.projectId"
      :mode="state.sensitiveMode"
      @update:mode="action.setSensitiveMode"
    />
  </div>
  <div class="sections-layout">
    <OverviewSection :icon="PhInfo" :title="t('overview.sections.information')">
      <div class="overview-layout">
        <div class="overview-main" ref="mainRef">
          <ProjectMetaDataData
            :project-id="state.projectId"
            :mode="state.mode.projectMetaData"
          />
        </div>
        <div
          class="overview-side"
          :style="
            state.sideHeight != null ? { height: `${state.sideHeight}px` } : {}
          "
        >
          <DebitNormsData
            :project-id="state.projectId"
            :mode="state.mode.debitNorms"
          />
        </div>
      </div>
      <div class="notes-layout">
        <ProjectNotesData
          :project-id="state.projectId"
          :mode="state.mode.projectNotes"
        />
        <AfterMarketTableData
          :project-id="state.projectId"
          :mode="state.mode.afterMarketTable"
        />
      </div>
    </OverviewSection>
    <OverviewSection :icon="PhTable" :title="t('overview.sections.ata')">
      <OverviewAtaTables :project-id="state.projectId" />
    </OverviewSection>
    <OverviewSection
      :icon="PhBuildings"
      :title="t('overview.sections.organisations')"
    >
      <div class="roles-layout">
        <ProjectOrganisationsData :project-id="state.projectId" />
      </div>
    </OverviewSection>
    <OverviewSection
      :icon="PhStairs"
      :title="t('overview.sections.liftPlanCesg')"
    >
      <LiftPlanData />
    </OverviewSection>
    <OverviewSection
      v-if="state.user && hasRole(UserRole.CESG, toValue(state.user))"
      :icon="PhStairs"
      :title="t('overview.sections.liftPlanUe')"
    >
      <LiftPlanUEListData
        :project-id="state.projectId"
        :user="toValue(state.user)!"
      />
    </OverviewSection>
  </div>
  <div
    class="floating-actions"
    v-if="state.user && hasRole(UserRole.CESG, toValue(state.user))"
  >
    <DefaultReviewerSelectorData
      :project-id="state.projectId"
      :mode="state.mode.defaultReviewerSelector"
    />
  </div>
</template>

<style scoped>
.overview-side-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.sections-layout {
  display: flex;
  flex-direction: column;
  gap: 4rem;
}

.overview-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: flex-start;
}

.overview-main {
  flex: 0 0 70%;
  min-width: 0;
}

.overview-side {
  flex: 1;
  min-width: 0;
}

@media (max-width: 1200px) {
  .overview-main,
  .overview-side {
    flex: 0 0 100%;
  }
}

.notes-layout {
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
  align-items: flex-start;
}

.notes-layout > * {
  flex: 1;
  min-width: 0;
}

@media (max-width: 768px) {
  .notes-layout {
    flex-direction: column;
  }
}

.roles-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: flex-start;
}

.roles-layout > * {
  flex: 1;
  min-width: 0;
}

@media (max-width: 768px) {
  .roles-layout > * {
    flex: 0 0 100%;
  }
}

.floating-actions {
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
